import apiService from '../../../services/api'
import { openExternalPaymentLink } from '../utils/openExternalPaymentLink'

/**
 * СБП #2 (Система Быстрых Платежей) Payment API Service
 * Альтернативный сервис для работы с оплатой через СБП (только для РФ)
 */

/**
 * Получить конфигурацию СБП #2 (курсы валют и лимиты)
 * @returns {Promise<Object>} Конфигурация с курсами и лимитами
 * @returns {number} min_rub - Минимальная сумма в рублях
 * @returns {number} max_rub - Максимальная сумма в рублях
 * @returns {number} price_one_crystal_in_rub - Стоимость 1 кристалла в рублях
 * @returns {number} price_one_rub_in_crystal - Стоимость 1 рубля в кристаллах
 */
export const fetchSbp2Config = async () => {
  try {
    const response = await apiService.makeRequest('/api/sbp2/')

    if (response.sbp2) {
      return response.sbp2
    } else {
      throw new Error('Invalid response format: sbp2 config not found')
    }
  } catch (error) {
    console.error('Error fetching SBP2 config:', error)
    throw error
  }
}

/**
 * Получить доступные пакеты для СБП #2 из общего списка тарифов
 * Фильтрует только те пакеты, у которых есть поле sbp2
 * @returns {Promise<Array>} Массив пакетов с ценами в рублях
 */
export const fetchSbp2Packages = async () => {
  try {
    const response = await apiService.makeRequest('/api/topup/')

    if (response.topup && Array.isArray(response.topup)) {
      // Фильтруем только пакеты с полем sbp2
      const sbpPackages = response.topup.filter(pkg => pkg.sbp2 !== undefined)
      return sbpPackages
    } else {
      throw new Error('Invalid response format: topup array not found')
    }
  } catch (error) {
    console.error('Error fetching SBP2 packages:', error)
    throw error
  }
}

/**
 * Создать платеж СБП #2 по количеству кристаллов
 * @param {number} crystals - Количество кристаллов для покупки
 * @returns {Promise<{link: string}>} Ссылка на оплату
 */
export const createPaymentByCrystals = async (crystals) => {
  try {
    const response = await apiService.makeRequest('/api/sbp2/', {
      method: 'POST',
      body: JSON.stringify({ crystal: crystals })
    })

    if (response.error) {
      // Обработка специфичных ошибок
      if (response.error === 'error sum') {
        throw new Error('INVALID_AMOUNT')
      } else if (response.error === 'error backend') {
        throw new Error('BACKEND_ERROR')
      } else {
        throw new Error(response.error)
      }
    }

    if (response.link) {
      return { link: response.link }
    } else {
      throw new Error('Invalid response format: link not found')
    }
  } catch (error) {
    console.error('Error creating SBP2 payment by crystals:', error)
    throw error
  }
}

/**
 * Создать платеж СБП #2 по сумме в рублях
 * @param {number} rubles - Сумма в рублях
 * @returns {Promise<{link: string}>} Ссылка на оплату
 */
export const createPaymentByRubles = async (rubles) => {
  try {
    const response = await apiService.makeRequest('/api/sbp2/', {
      method: 'POST',
      body: JSON.stringify({ rub: rubles })
    })

    if (response.error) {
      // Обработка специфичных ошибок
      if (response.error === 'error sum') {
        throw new Error('INVALID_AMOUNT')
      } else if (response.error === 'error backend') {
        throw new Error('BACKEND_ERROR')
      } else {
        throw new Error(response.error)
      }
    }

    if (response.link) {
      return { link: response.link }
    } else {
      throw new Error('Invalid response format: link not found')
    }
  } catch (error) {
    console.error('Error creating SBP2 payment by rubles:', error)
    throw error
  }
}

/**
 * Открыть ссылку на оплату СБП #2 через Telegram WebApp API
 * @param {string} paymentLink - Ссылка на оплату
 * @param {Function} onRedirect - Callback после редиректа
 * @param {Object} paymentWindow - Окно оплаты
 */
export const openSbp2Payment = (paymentLink, onRedirect, paymentWindow = null) => {
  openExternalPaymentLink(paymentLink, onRedirect, 'SBP2', paymentWindow)
}

// Алиасы для совместимости с sbpApi (чтобы переиспользовать общий UI-компонент)
export const fetchSbpConfig = fetchSbp2Config
export const fetchSbpPackages = fetchSbp2Packages
export const openSbpPayment = openSbp2Payment

// Экспорт как объект для удобства
export default {
  fetchSbp2Config,
  fetchSbp2Packages,
  createPaymentByCrystals,
  createPaymentByRubles,
  openSbp2Payment,
  fetchSbpConfig,
  fetchSbpPackages,
  openSbpPayment
}
